// SPDX-License-Identifier: LicenseRef-MMS-WIRE
// Copyright (c) 2016-2026 Metal Management Solutions (Pty) Ltd

import {ActivatedRouteSnapshot, CanActivateChildFn, CanActivateFn, Router, RouterStateSnapshot, UrlTree} from '@angular/router';
import {AuthGuardData, createAuthGuard} from "keycloak-angular";
import {inject} from "@angular/core";
import {AppScope} from "../app_scope.service";
import {firstValueFrom} from "rxjs";

/**
 * Guard to assert the user context is loaded. Branches on the bootstrap outcome:
 *   loaded + onboarded   → allow.
 *   loaded + not onboarded → redirect to /onboarding (existing onboarding flow).
 *   failed / 'noaccount'   → redirect to /onboarding (the <src-no-account> overlay anchors there).
 *   failed / 'unexpected'  → cancel navigation only. The user stays on the URL they requested
 *                            and AppScope.handleAuthFailure's snackbar is the sole feedback.
 *                            We must NOT redirect to /onboarding here — that would show the wrong
 *                            URL for a transient 5xx and confuse the user (see WIRE review).
 */
const isUserContextLoaded = async (
    route: ActivatedRouteSnapshot,
    _: RouterStateSnapshot,
    authData: AuthGuardData): Promise<boolean | UrlTree> => {
    const appScope = inject(AppScope);
    const router: Router = inject(Router);

    const state = await firstValueFrom(appScope.bootstrapResolved$);

    if (state.status === 'loaded') {
        if (state.user?.is_onboarded) {
            return true;
        }
        router.navigate(['onboarding']);
        return false;
    }

    if (state.reason === 'noaccount') {
        router.navigate(['onboarding']);
        return false;
    }

    return false;
};

export const userContextLoadedGuard = createAuthGuard<CanActivateFn | CanActivateChildFn>(isUserContextLoaded);
